import type { Scan, ScanConfirmOptions, ScpData, SealedInfo } from "@hobbyshopify/backend-client";
import { centsToInput, inputToCents, isYearValid } from "./scan-edit";

/**
 * The confirm form for sealed product, the counterpart of `scan-edit.ts`.
 *
 * The identity fields decide **which product this is**: set, box type and
 * variant are what the shop's item is keyed on, and what the price is looked
 * up by, so correcting one moves both.
 */
export type SealedEditState = {
  category: string;
  /** Only for sports product; "" for none. */
  sport: string;
  brand: string;
  year: string;
  setName: string;
  productType: string;
  productVariant: string;
  configuration: string;
  language: string;
  productFullName: string;
  price: string;
  floorPrice: string;
};

export const SEALED_CATEGORY_LABELS: Record<string, string> = {
  sports: "Sports",
  pokemon: "Pokémon",
  mtg: "Magic: The Gathering",
  yugioh: "Yu-Gi-Oh!",
  one_piece: "One Piece",
  lorcana: "Lorcana",
  other_tcg: "Other TCG",
  non_sport: "Non-sport",
};

/** Suggestions for the sport field; any other the source knows is accepted. */
export const COMMON_SPORTS = ["Baseball", "Basketball", "Football", "Hockey", "Soccer", "Racing", "Wrestling", "UFC"];

export const SEALED_PRODUCT_TYPE_LABELS: Record<string, string> = {
  hobby_box: "Hobby box",
  jumbo_box: "Jumbo box",
  blaster_box: "Blaster box",
  mega_box: "Mega box",
  hanger_box: "Hanger box",
  retail_box: "Retail box",
  booster_box: "Booster box",
  booster_bundle: "Booster bundle",
  elite_trainer_box: "Elite Trainer Box",
  collection_box: "Collection box",
  tin: "Tin",
  pack: "Pack",
  case: "Case",
  other: "Other",
};

export function isSealedScan(scan: Pick<Scan, "itemKind"> | null | undefined): boolean {
  return scan?.itemKind === "sealed";
}

export function toSealedEditState(scan: Scan): SealedEditState {
  const info = scan.sealedInfo;
  const item = scan.item;
  return {
    category: info?.category ?? "sports",
    sport: info?.sport ?? "",
    brand: info?.brand ?? "",
    year: info?.year != null ? String(info.year) : "",
    setName: info?.set_name ?? "",
    productType: info?.product_type ?? "other",
    productVariant: info?.product_variant ?? "",
    configuration: info?.configuration ?? "",
    language: info?.language ?? "English",
    productFullName: info?.product_full_name ?? "",
    price: centsToInput(scan.draftPriceCents ?? suggestedSealedPriceCents(scan)),
    floorPrice: centsToInput(item?.floorPriceCents),
  };
}

/** What the asking price follows until the reviewer types one: a matched item's own price, else the estimate. */
export function suggestedSealedPriceCents(scan: Scan): number | null {
  return scan.item?.currentPriceCents ?? scan.estimatedPriceCents;
}

/** The asking price to save with a draft: a typed one, null to hand it back to the estimate, or nothing to change. */
export function draftPriceCents(scan: Scan, edit: SealedEditState, manual: boolean): number | null | undefined {
  const saved = scan.draftPriceCents ?? null;
  if (!manual) return saved === null ? undefined : null;
  const cents = inputToCents(edit.price);
  return cents == null || cents <= 0 || cents === saved ? undefined : cents;
}

const SEALED_FIELDS: Partial<Record<keyof SealedEditState, keyof SealedInfo>> = {
  category: "category",
  sport: "sport",
  brand: "brand",
  year: "year",
  setName: "set_name",
  productType: "product_type",
  productVariant: "product_variant",
  configuration: "configuration",
  language: "language",
  productFullName: "product_full_name",
};

const shown = (value: unknown): string => (value == null ? "" : String(value).trim());

/** What the scanner read for each field the reviewer has since changed, for "Scanner: …" under it. */
export function scannerReadings(scan: Scan, edit: SealedEditState): Partial<Record<keyof SealedEditState, string>> {
  const model = scan.modelSealedInfo;
  if (!model) return {};
  const out: Partial<Record<keyof SealedEditState, string>> = {};
  for (const [field, key] of Object.entries(SEALED_FIELDS) as [keyof SealedEditState, keyof SealedInfo][]) {
    const read = shown(model[key]);
    if (read.toLowerCase() !== shown(edit[field]).toLowerCase()) out[field] = read || "—";
  }
  return out;
}

/** Only the fields the reviewer changed, diffed against the reading in force. */
export function buildSealedCorrections(scan: Scan, edit: SealedEditState): Partial<SealedInfo> {
  const info: Partial<SealedInfo> = scan.sealedInfo ?? {};
  const corrections: Record<string, unknown> = {};
  const put = (key: keyof SealedInfo, was: string | null | undefined, now: string) => {
    if ((was ?? "") !== now) corrections[key] = now;
  };

  put("category", info.category, edit.category);
  put("brand", info.brand, edit.brand);
  put("set_name", info.set_name, edit.setName);
  put("product_type", info.product_type, edit.productType);
  put("product_variant", info.product_variant, edit.productVariant);
  put("configuration", info.configuration, edit.configuration);
  put("language", info.language, edit.language);
  put("product_full_name", info.product_full_name, edit.productFullName);
  const year = edit.year ? Number(edit.year) : null;
  if (year !== (info.year ?? null)) corrections.year = year;
  // A sport only means something for sports product.
  const sport = edit.category === "sports" ? edit.sport.trim() || null : null;
  if (sport !== (info.sport ?? null)) corrections.sport = sport;

  return corrections as Partial<SealedInfo>;
}

export function buildSealedConfirmBody(scan: Scan, edit: SealedEditState): ScanConfirmOptions {
  const corrections = buildSealedCorrections(scan, edit);
  const body: ScanConfirmOptions = {};
  if (Object.keys(corrections).length > 0) body.corrections = corrections;
  const priceCents = inputToCents(edit.price);
  if (priceCents != null) body.priceCents = priceCents;
  const floorCents = inputToCents(edit.floorPrice);
  if (floorCents != null) body.floorPriceCents = floorCents;
  return body;
}

export function isSealedEditValid(edit: SealedEditState): boolean {
  const price = inputToCents(edit.price);
  const floor = inputToCents(edit.floorPrice);
  return isYearValid(edit.year) && edit.setName.trim() !== "" && (price == null || floor == null || floor <= price);
}

const positive = (value: unknown): number | null => (typeof value === "number" && value > 0 ? value : null);

/**
 * What the pricing source says the product is worth: the market price, and the
 * dealer buy and sell prices — a guide to what to pay and what to ask.
 */
export function sealedGuides(scpData: ScpData | null | undefined): {
  marketCents: number | null;
  dealerBuyCents: number | null;
  dealerSellCents: number | null;
} {
  const record = (scpData ?? {}) as Record<string, unknown>;
  return {
    marketCents: positive(record["loose-price"]),
    dealerBuyCents: positive(record["retail-loose-buy"]),
    dealerSellCents: positive(record["retail-loose-sell"]),
  };
}

/** "Hobby box · Logofractor" — the second line of a sealed row. */
export function sealedSubtitle(info: Pick<SealedInfo, "product_type" | "product_variant" | "configuration">): string {
  return [SEALED_PRODUCT_TYPE_LABELS[info.product_type], info.product_variant, info.configuration]
    .filter(Boolean)
    .join(" · ");
}

/** A pending sealed scan the matcher placed on no item of the shop's own: staff may link it to a hand-made one. */
export function isLinkableScan(scan: Scan): boolean {
  return (
    isSealedScan(scan) &&
    scan.review === "PENDING" &&
    scan.recognitionOutcome !== "no_match" &&
    (scan.matchKind === "NONE" || scan.matchKind === "CREATED")
  );
}

/** The scan's product barcode, if it read one. */
export function scanBarcode(scan: Scan): string | null {
  const code = scan.suggestedCodes.find((c) => c.type === "upc" || c.type === "ean");
  return code?.value ?? (scan.sealedInfo?.upc || null);
}

/** What the picker searches first: the barcode, else the set. Nothing is picked for the reviewer. */
export function initialLinkSearch(scan: Scan): string {
  return scanBarcode(scan) ?? scan.sealedInfo?.set_name ?? "";
}

/** The scan's market price, for staff who want the linked item to ask it. */
export function scanMarketCents(scan: Scan): number | null {
  return scan.marketEstimate?.marketCents ?? scan.estimatedPriceCents;
}
